import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Youtube, Quote } from "lucide-react";
import useLenis from "../hooks/useLenis";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import FloatingIcons from "../components/site/FloatingIcons";
import AnimatedLogo from "../components/site/AnimatedLogo";
import StrengthVideos from "../components/site/StrengthVideos";

const TEAM = [
    { name: "Mohit Dubey", role: "Senior Technical SEO Expert", photo: "/teams/Mohit.jpg.jpeg" },
    { name: "Harsh Khatwani", role: "Financial Advisor", photo: "/teams/Harsh.jpg.jpeg" },
    { name: "Ishan Basantani", role: "Senior Graphic Designer", photo: "/teams/Ishan.jpg.jpeg" },
    { name: "Kashish Kamora", role: "Senior Social Media Manager", photo: "/teams/Kashish.JPG.jpeg" },
    { name: "Nikhar Rajdev", role: "SEO Expert", photo: "/teams/Nikhar.JPG.jpeg" },
    { name: "Muskan Narang", role: "Social Media Manager", photo: "/teams/Muskan.jpg.jpeg" },
    { name: "Ammar Tinwala", role: "Graphic Designer & Video Editor", photo: "/teams/Ammar.jpg.jpeg" },
    { name: "Harshita Singh Baghel", role: "Content Writer", photo: "/teams/Harshita.jpg.jpeg" },
    { name: "Manya Rajdev", role: "Creative Support", photo: "/teams/Manya.JPG.jpg.jpeg" },
    { name: "Disha Kingrani", role: "Social Media Manager", photo: "/teams/Disha.jpeg" },
    { name: "Kunj Vani", role: "Video Editor", photo: "/teams/Kunj.JPG.jpeg" },
    { name: "Priyanka Khatri", role: "Intern", photo: "/teams/Priyanka.jpg.jpeg" },
    { name: "Mahika Jagwani", role: "Graphic Designer", photo: "/teams/Mahika.jpg.jpeg" },
];

const PODCAST_VIDEOS = [
    {
        id: "8DeEkJZtY-4",
        title: "Our Strength Stories - Episode 1",
        url: "https://www.youtube.com/watch?v=8DeEkJZtY-4",
        thumb: "https://i.ytimg.com/vi/8DeEkJZtY-4/hqdefault.jpg",
    },
    {
        id: "4_LV90ms5gQ",
        title: "Our Strength Stories - Episode 2",
        url: "https://www.youtube.com/watch?v=4_LV90ms5gQ",
        thumb: "https://i.ytimg.com/vi/4_LV90ms5gQ/hqdefault.jpg",
    },
    {
        id: "gLcmja8WBwM",
        title: "Our Strength Stories - Episode 3",
        url: "https://www.youtube.com/watch?v=gLcmja8WBwM&t=91s",
        thumb: "https://i.ytimg.com/vi/gLcmja8WBwM/hqdefault.jpg",
    },
];

export default function About() {
    useLenis();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    return (
        <div data-testid="about-page" className="relative">
            <Navbar />

            {/* HERO */}
            <section
                data-testid="about-hero"
                className="relative min-h-[100svh] w-full overflow-hidden bg-white pt-36 lg:pt-44 pb-24"
            >
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none opacity-[0.5]"
                    style={{
                        backgroundImage:
                            "radial-gradient(rgba(15,61,122,0.07) 1px, transparent 1px)",
                        backgroundSize: "26px 26px",
                    }}
                />
                <FloatingIcons tone="light" />

                <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <p className="label-eyebrow">About Us</p>
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-5 font-display font-bold uppercase text-xl sm:text-2xl lg:text-4xl tracking-tight text-communiti-text"
                        >
                            WELCOME TO <span className="text-communiti">COMMMMUNITI</span>
                        </motion.h1>

                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.2 }}
                            className="mt-6 font-display font-bold uppercase text-[11vw] sm:text-6xl lg:text-[6.4rem] tracking-tight leading-[0.92] text-communiti-text"
                        >
                            WHERE
                            <br />
                            MARKETING ISN'T
                            <br />
                            JUST VISIBILITY;
                            <br />
                            <span className="text-communiti">ITS IMPACT.</span>
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.4 }}
                            className="mt-10 max-w-xl text-base text-communiti-muted leading-relaxed"
                        >
                            We are a digital marketing agency in Indore, created with a
                            vision to help brands grow through clarity, creativity, and
                            performance. At Commmmuniti, we don't just focus on
                            visibility; we focus on building connections that drive real
                            engagement and measurable growth. Every idea we craft is
                            shaped with intent, designed to resonate with the right
                            audience, and executed to deliver meaningful digital impact.
                        </motion.p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className="lg:col-span-5 flex justify-center"
                    >
                        <AnimatedLogo size={420} ring={150} />
                    </motion.div>
                </div>
            </section>

            {/* OUR STORY */}
            <section
                data-testid="about-story"
                className="relative py-24 lg:py-32 text-white"
                style={{ backgroundColor: "#0d3a6e" }}
            >
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9 }}
                            className="mt-4 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1]"
                        >
                            Our Story:
                            <br />
                            Built from
                            <br />
                            <span className="font-serif-it font-normal">conversations</span>
                        </motion.h2>
                    </div>
                    <div className="lg:col-span-8 space-y-6 text-base lg:text-lg leading-relaxed text-white/85 max-w-3xl">
                        <p>It didn't begin with an office. It began with ideas.</p>
                        <p>
                            Ideas shared in conversations, about brands, about what was
                            missing, and about how marketing had started to feel
                            disconnected from the people it was meant for.
                        </p>
                        <p>
                            A small group of thinkers came together with one shared
                            belief: brands don't fail because they lack marketing, they
                            fail because they lack connection.
                        </p>
                        <p>
                            From that belief,{" "}
                            <span className="font-bold brand-name">Commmmuniti</span> was born.
                        </p>
                        <p>
                            Today, Commmmuniti stands as a growing team of designers,
                            creators, and storytellers, working with one purpose: to make
                            communication more meaningful and marketing more human again,
                            and to establish itself as the best digital marketing agency
                            in Indore.
                        </p>
                    </div>
                </div>
            </section>

            {/* THE FOUNDER */}
            <section
                data-testid="about-founder"
                className="relative bg-white py-24 lg:py-32"
            >
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9 }}
                        className="lg:col-span-5"
                    >
                        <div className="relative aspect-[4/5] rounded-3xl overflow-hidden bg-communiti-surface">
                            <img
                                src="/teams/Roshni.jpg"
                                alt="Roshni Pahuja, Founder of Commmmuniti"
                                className="h-full w-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                            />
                            <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 flex items-end justify-between">
                                <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-[0.2em] text-communiti-muted">
                                        Founder
                                    </p>
                                     <p className="font-display text-[0.625rem] font-bold text-communiti-text">
                                        Roshni Pahuja
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <div className="lg:col-span-7">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9 }}
                            className="mt-4 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-communiti-text"
                        >
                            The Founder
                        </motion.h2>
                        <div className="mt-8 space-y-5 text-base lg:text-lg leading-relaxed text-communiti-muted max-w-2xl">
                            <p>
                                <span className="brand-name">Commmmuniti</span> began with a simple belief that brands don't
                                fail because they lack visibility, but because they lack
                                meaning.
                            </p>
                            <p>
                                Founded by{" "}
                                <span className="text-communiti-text font-medium">
                                    Roshni Pahuja
                                </span>
                                , Commmmuniti is based in Indore but is not limited to it.
                                Her vision was to create a space where strategy meets
                                storytelling, where creativity is backed by performance,
                                and where every brand is shaped with intention, not noise.
                            </p>
                            <p>
                                What started as an idea is now a growing collective working
                                with one purpose to build digital identities that don't
                                just exist, but resonate.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* STRENGTH STORIES (PODCAST) */}
            <section
                data-testid="about-strength"
                className="relative text-white py-24 lg:py-32"
                style={{ backgroundColor: "#fa5a44" }}
            >
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-10 items-end pb-12">
                            <div className="lg:col-span-7">
                                <motion.h2
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.9 }}
                                className="mt-4 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
                            >
                                Our Strength
                                <br />
                                <span className="font-serif-it font-normal text-communiti-soft">
                                    Stories
                                </span>
                            </motion.h2>
                            <p className="mt-5 font-display text-2xl text-white/70">
                                Conversations that carry weight.
                            </p>
                        </div>
                        <div className="lg:col-span-5">
                            <p className="text-base text-white/65 leading-relaxed">
                                Our Strength Stories is the voice of Commmmuniti beyond
                                campaigns and clients, a podcast space where journeys are
                                not polished, but real. Here, founders, creators, and
                                thinkers share what growth actually looks like behind the
                                surface. It's not about success stories, it's about the
                                struggle, the shift, and the strength it takes to build
                                something meaningful.
                            </p>
                            <a
                                href="https://www.youtube.com/@OurStrengthStories"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="strength-youtube-link"
                                className="mt-6 inline-flex items-center gap-2 text-sm text-white/80 hover:text-white"
                            >
                                <Youtube className="h-4 w-4" />
                                Watch on YouTube
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                            <a
                                href="https://www.youtube.com/@OurStrengthStories?sub_confirmation=1"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="strength-subscribe"
                                className="mt-3 inline-flex items-center gap-2 rounded-full bg-white text-black px-4 py-2 text-sm font-medium hover:bg-communiti-soft hover:text-white transition-colors"
                            >
                                <Youtube className="h-4 w-4" />
                                Subscribe
                            </a>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {PODCAST_VIDEOS.map((v, i) => (
                            <motion.a
                                key={v.id}
                                href={v.url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`about-podcast-video-${i}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                                className="group relative rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03]"
                            >
                                <div className="relative aspect-video overflow-hidden">
                                    <img
                                        src={v.thumb}
                                        alt={v.title}
                                        loading="lazy"
                                        className="h-full w-full object-cover scale-105 group-hover:scale-100 transition-transform duration-[1200ms]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                                    <div className="absolute bottom-3 left-3 right-3">
                                        <h3 className="font-display text-[0.4375rem] leading-snug line-clamp-2 text-white">
                                            {v.title}
                                        </h3>
                                    </div>
                                </div>
                            </motion.a>
                        ))}
                    </div>

                    <StrengthVideos />
                </div>
            </section>

            {/* TEAM */}
            <section
                data-testid="about-team"
                className="relative bg-white py-24 lg:py-32"
            >
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-10 items-end pb-14">
                            <div className="lg:col-span-7">
                                <motion.h2
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.9 }}
                            className="mt-4 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-communiti-text"
                            >
                                <span className="brand-name">The Commmmuniti</span> Team
                            </motion.h2>
                        </div>
                        <div className="lg:col-span-5 max-w-md text-base text-communiti-muted leading-relaxed">
                            We are not defined by roles, but by rhythm — a shared way of
                            thinking, creating, and executing.
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {TEAM.map((m, i) => (
                            <motion.div
                                key={m.name}
                                data-testid={`team-card-${i}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.1 }}
                                transition={{
                                    duration: 0.6,
                                    delay: (i % 12) * 0.05,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="group relative rounded-2xl border border-communiti-line bg-communiti-surface/40 p-5 hover:border-communiti hover:-translate-y-1 transition-all duration-500"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden rounded-xl bg-communiti-surface mb-4">
                                    {m.photo ? (
                                        <img
                                            src={m.photo}
                                            alt={m.name}
                                            loading="lazy"
                                            className="h-full w-full object-cover"
                                        />
                                    ) : (
                                        <span className="font-display text-3xl font-bold text-communiti/30">
                                            {m.name
                                                .split(" ")
                                                .map((p) => p[0])
                                                .slice(0, 2)
                                                .join("")}
                                        </span>
                                    )}
                                </div>
                                <p className="font-display font-bold text-base lg:text-lg text-communiti-text leading-tight">
                                    {m.name}
                                </p>
                                <p className="mt-1 text-xs text-communiti-muted leading-tight">
                                    {m.role}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CULTURE */}
            <section
                data-testid="about-culture"
                className="relative text-white py-24 lg:py-32 overflow-hidden"
                style={{ backgroundColor: "#0d3a6e" }}
            >
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[600px] w-[1200px] rounded-full blur-[120px] opacity-30 pointer-events-none"
                    style={{ background: "radial-gradient(closest-side, #0F3D7A, transparent)" }}
                />
                <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 text-center">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9 }}
                            className="mt-5 font-display font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tight"
                    >
                        Our Culture
                    </motion.h2>

                    <motion.blockquote
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.2 }}
                        className="mt-14 max-w-4xl mx-auto"
                    >
                        <Quote className="mx-auto h-8 w-8 text-communiti-soft" />
                        <div className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl leading-snug text-white space-y-6">
                            <p>
                                At Commmmuniti, collaboration is not a process; it is the
                                foundation.
                            </p>
                                <p className="text-white/75 text-xl sm:text-2xl lg:text-3xl font-normal">
                                Ideas don't belong to individuals here; they evolve through
                                conversation, conflict, and clarity. Every project is
                                treated like a story, shaped carefully, executed
                                intentionally, and delivered with purpose.
                            </p>
                            <p>
                                We don't chase trends.{" "}
                                <span className="font-serif-it text-communiti-soft">
                                    We build relevance.
                                </span>
                            </p>
                            <p>
                                We don't create noise.{" "}
                                <span className="font-serif-it text-communiti-soft">
                                    We create impact.
                                </span>
                            </p>
                        </div>
                    </motion.blockquote>

                    <div className="mt-16">
                        <a
                            data-testid="culture-cta"
                            href="tel:+917987924110"
                            className="group inline-flex items-center gap-2 rounded-full bg-communiti text-white pl-6 pr-2 py-2.5 text-sm font-medium hover:bg-white hover:text-black transition-colors"
                        >
                            LET'S TALK
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-black group-hover:rotate-45 transition-transform duration-300">
                                <ArrowUpRight className="h-4 w-4" />
                            </span>
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
